<template>
  <!-- デフォルトでbackground-colorを設定しているが、時間に合わせて動的に変化させる -->
  <div
    class="whole"
    ref="whole"
    :style="{
      backgroundColor: `${this.$data.backgroundColor}`,
    }"
  >
    <vue-progress-bar />

    <template v-if="!$data.isSoundLoadCompleted">
      <Modal />
    </template>

    <Menu :gainValue="$data.gainValue" @menuClick="onMenuclick" />

    <About :isMenuOpen="$data.isMenuOpen" :worldWidth="$data.setting.worldWidth" @overlayClick="onMenuclick" />

    <template v-if="_isBalancedEnvironment">
      <div class="logo">
        <img src="../assets/logo/logo-vertical.svg" />
      </div>
    </template>

    <Wiki :agent="$data.focusingAgent" :backgroundColor="`${this.$data.backgroundColor}`" ref="wiki" />

    <template v-for="agent in $data.agents">
      <template v-if="agent.isEndingLife">
        <img
          :class="agent.type === 'agent_4' ? 'agent end-life-type-4' : 'agent end-life'"
          :src="agent.src"
          :key="agent.id"
          :style="{
            position: 'fixed',
            width: agent.width + 'px',
            height: agent.height + 'px',
            top: agent.y + 'px',
            left: agent.x + 'px',
            transform: `rotate(${agent.theta}deg)`,
          }"
          @mouseover="onAgentFocus(agent)"
          @mouseleave="onAgentLeave"
        />
      </template>
      <template v-else>
        <img
          :class="`agent ${agent.class}`"
          :src="agent.src"
          :key="agent.id"
          :style="{
            position: 'fixed',
            width: agent.width + 'px',
            height: agent.height + 'px',
            top: agent.y + 'px',
            left: agent.x + 'px',
            transform: `rotate(${agent.theta}deg)`,
          }"
          @mouseover="onAgentFocus(agent)"
          @mouseleave="onAgentLeave"
        />
      </template>
    </template>

    <canvas
      key="eclipseCanvas"
      :class="_eclipseCanvasClass"
      :width="_canvasWidth.eclipse * _scale"
      :height="_canvasHeight.eclipse * _scale"
      :style="`width: ${_canvasWidth.eclipse}px; height: ${_canvasHeight.eclipse}px`"
      ref="eclipseCanvas"
    />

    <Function
      :lifeDeathRatios="$data.lifeDeathRatios"
      :isSoundLoadComplete="$data.isSoundLoadCompleted"
      @runPause="onRunPause"
      @runReset="onRunReset"
      @runRestore="onRunRestore"
    />

    <SideBar
      :agentGainer="$data.agentGainer"
      :droneGainer="$data.droneGainer"
      :lifeDeathRatios="$data.lifeDeathRatios"
      :agents="$data.agents"
      :nextEclipseDate="$data.nextEclipseDate"
      :nowDate="$data.nowDate"
      :elapsedTime="$data.elapsedTime"
      :backgroundColor="$data.backgroundColor"
      @agentGainerValueChange="onAgentGainerValueChange"
      @droneGainerValueChange="onDroneGainerValueChange"
    />

    <GrapthCanvas
      ref="grapthCanvases"
      :canvasWidth="_canvasWidth.lineAndBar"
      :canvasHeight="_canvasHeight.lineAndBar"
      :scale="_scale"
    />
  </div>
</template>

<script>
// libraries
import * as d3 from 'd3';
import { differenceInMilliseconds, differenceInSeconds, closestIndexTo } from 'date-fns';
import _ from 'lodash';

// components
import Menu from '@/components/Menu.vue';
import About from '@/components/About.vue';
import Wiki from '@/components/Wiki.vue';
import Modal from '@/components/Modal.vue';
import Function from '@/components/Function.vue';
import SideBar from '@/components/SideBar.vue';
import GrapthCanvas from '@/components/GrapthCanvas.vue';

// constants
import CONSTANTS from '@/constants/index.js';

// utils
import Audio from '../utils/audio';
import Swim from '../utils/swim';
import Functions from '../utils/functions';

// css
import '@/assets/css/animation.scss';

// assets
import AGENT_SVGS from '../assets/images/agents/index.js';
import SOUNDS from '../assets/sounds/index.js';

export default {
  components: {
    Menu,
    About,
    Modal,
    Wiki,
    Function,
    SideBar,
    GrapthCanvas,
  },
  data() {
    const setting = CONSTANTS.BASE_SETTING;
    const lifeDeathRatios = _.cloneDeep(CONSTANTS.LIFE_DEATH_RATIOS); // 操作するのでメモリ領域分ける

    return {
      setting,
      lifeDeathRatios,
      agents: [],
      audioInstance: null,
      previousEclipseDate: null,
      nextEclipseDate: null,
      nowDate: null,
      nowRate: null,
      createdDate: null,
      elapsedTime: '',
      d3_objects: {},
      isSoundLoadCompleted: false,
      hitEclipseDate: null,
      focusingAgent: {},
      backgroundColor: 'rgb(255, 255, 255)', // デフォルト値rgb(255, 252, 241)だったが一時的に真っ白にする
      isMenuOpen: false,
      isPause: true,
      eclipseMovementFase: 0, // 0: 停止, 1: 始まり, 2: 最大化, 3: 終わり
      agentGainer: { value: '1' }, // agentのgainを操作する
      droneGainer: { value: '1' }, // droneのgainを操作する
      isEclipseRetrograde: true, // 月と太陽の位置が近づいていくのか、逆の方向でいくのか。
      playingDroneSourceName: '', // 今再生されているもの
      playedDroneSourceName: '', // 一つ前に再生されたもの
      isDronePlayed: false,
    };
  },
  watch: {
    nowDate(value) {
      if (!this.$data.nextEclipseDate) {
        return;
      }

      const diff = differenceInSeconds(this.$data.nextEclipseDate, value);

      // stringsはこの時間、なっていないはずなので、なっていたら止めとく。
      if (406 < diff) {
        if (this.$data.isStringsPlayed) {
          // TODO いきなり止めるのではなくて、フェードアウトする
          this.stopDroneSound('ENV_STRINGS');
          this.$data.isStringsPlayed = false;
        }
      }

      // 0.5分前=30秒前と8分前=406秒前の間を第一段階とし、ストリングスを流す。ポーズ中であればスキップする
      // ストリングスの曲自体は6分16秒。秒換算では376秒。diffが30の時になりやむことを考えると、406秒前にならす。
      if (30 < diff && diff <= 406) {
        // 再生ボタンが押されている時だけ、発動させる。
        if (!this.$data.isStringsPlayed && !this.$data.isPause) {
          // nextEclipseDateから406秒前時点でstringsを再生するので、406秒時点からの経過時間がoffsetになる。
          const offset = 406 - diff;

          this.playDroneSound('ENV_STRINGS', 0.6, offset, true);
          this.$data.isStringsPlayed = true;
        }
      }

      // 0.5分前=30秒前を第二段階とし、ストリングスをとめ、日食用の音源を流す。
      if (diff <= 30) {
        if (this.$data.isStringsPlayed) {
          // TODO いきなり止めるのではなくて、フェードアウトする
          this.stopDroneSound('ENV_STRINGS');
          this.$data.isStringsPlayed = false;
        }

        // 再生ボタンが押されている時だけ、発動させる。
        if (!this.$data.isEclipsePlayed && !this.$data.isPause) {
          // TODO ENVの音もフェードアウトする

          // nextEclipseDateから30秒前時点でeclipseを再生するので、30秒時点からの経過時間がoffsetになる。
          const offset = 30 - diff;

          // TBだと確実に音割れるので変更
          const eclipseSource = 'ECLIPSE';

          // TBだと確実に音割れるので変更
          const eclipseGain = (() => {
            if (window.innerWidth < CONSTANTS.TB_BREAK_POINT) {
              return 0.6;
            }

            return 1.7;
          })();

          // 日食の音はloopなし
          this.playDroneSound(eclipseSource, eclipseGain, offset, false);
          this.$data.isEclipsePlayed = true;

          // 今の音源を止める
          this.stopDroneSound(this.$data.playingDroneSourceName);

          // agentがみんなburuburuしだすのもトリガーする
          this.$data.eclipseMovementFase = 1;

          this.$data.hitEclipseDate = this.$data.nextEclipseDate;
        }
      }

      if (diff <= 15 && this.$data.isEclipsePlayed) {
        // 激しくなる
        this.$data.eclipseMovementFase = 2;
      }

      // リセット処理。たまに画面離脱して戻ってきたりするとずっとeclipseの音がなることがあるので、eclipseの音がなるはずのない時間帯をチェックして、なってたら止める（30秒前と4分30前の間）
      if (30 < diff && diff <= 870) {
        if (this.$data.isEclipsePlayed) {
          this.stopDroneSound('ECLIPSE');
          this.$data.isEclipsePlayed = false;
          this.$data.eclipseMovementFase = 0;
        }
      }

      // 太陽と月の位置算出のために、現在日食の何%進行しているのかを算出
      const between = differenceInMilliseconds(this.$data.nextEclipseDate, this.$data.previousEclipseDate);
      const elapse = differenceInMilliseconds(this.$data.nowDate, this.$data.previousEclipseDate);
      this.$data.nowRate = elapse / between;

      // nextEclipseDateと同じ = 日食の時間になったら、描画のために逆行させる。
      if (diff <= 0) {
        // 日食が発生した後、日食が画面外に飛ばされるバグの理由はdiffが0の時に、nowRateが0.99992とかになっていること。これをなんとかして防ぎたい')
        // diff = nextEclipseとの差（秒）が0 => 普通に考えるとrateは100%になるはず。でもその状態でthis.$data.isEclipseRetrograde = true;をする実装をしているので、
        // 逆行した時のrateが99%??となり、基本的に描画ロジックにわたるrateが50を超える時は基本的にthis.$data.isEclipseRetrograde = falseの想定の時のみなので、バグる。
        // したがって、その辺の歪みを解決するために、無理やりrate0にする(でぐれおきそうだけどやってみる)
        this.$data.nowRate = 0;
        this.$data.isEclipseRetrograde = true;
      }

      // 以下は一度日食を通過してれば実行させる
      if (!this.$data.hitEclipseDate) {
        return;
      }

      const past = differenceInSeconds(value, this.$data.hitEclipseDate);

      // 15秒後、日食中のburuburuの動きを弱める
      if (15 <= past && past < 22) {
        this.$data.eclipseMovementFase = 3;
      }

      // 22秒後、日食中のburuburuの動きを止める
      if (22 <= past && past < 30) {
        this.$data.eclipseMovementFase = 0;
      }

      // 30秒後、一旦日食変化系をリセットさせる。
      if (30 <= past) {
        this.stopDroneSound('ECLIPSE');
        this.$data.isEclipsePlayed = false;
        this.$data.hitEclipseDate = null;

        // 今の音源を止める（念のため2回目）
        this.stopDroneSound(this.$data.playingDroneSourceName);

        // 次のdrone音源を決定する
        const randomPick = (() => {
          // 前回と違うものをpickupしたい
          const candidates = SOUNDS.droneSounds.filter((item) => {
            return item.soundName !== this.$data.playedDroneSourceName;
          });

          return candidates[Math.floor(Math.random() * candidates.length)];
        })();

        this.$data.playedDroneSourceName = this.$data.playingDroneSourceName;
        this.$data.playingDroneSourceName = randomPick.soundName;

        // 次のdrone音源を再生する
        const diff = differenceInSeconds(this.$data.nextEclipseDate, this.$data.nowDate);
        // 15分間=900秒からdiffを引けば何秒経過していたのかが分かる。音源は15分尺のものが前提
        const offset = 900 - diff;
        this.playDroneSound(randomPick.soundName, 0.28, offset, true);
      }
    },
  },
  methods: {
    initTime() {
      const time = new Date();
      this.$data.createdDate = time;
      this.$data.nowDate = time;
    },
    initSetting() {
      this.$data.setting.worldWidth = window.innerWidth;
      this.$data.setting.worldHeight = window.innerHeight;
    },
    initAudio() {
      this.$data.audioInstance = new Audio();
      this.$data.audioInstance.resume();

      const element = this.$refs.grapthCanvases.$el;
      const lineGraphContext = element.firstElementChild.getContext('2d');
      const barGraphContext = element.lastElementChild.getContext('2d');

      // 最初に再生するdroneの音を決める
      const randomPick = SOUNDS.droneSounds[Math.floor(Math.random() * SOUNDS.droneSounds.length)];
      this.$data.playingDroneSourceName = randomPick.soundName;
      this.$data.playedDroneSourceName = randomPick.soundName;

      // LineGraph関係
      lineGraphContext.scale(this._scale, this._scale);
      const renderLineGraphAnalyser = () => {
        // this.$data.audioInstance.renderLineGraphAnalyser(lineGraphContext, this.$refs.whole.style.backgroundColor);
        this.$data.audioInstance.renderLineGraphAnalyser(lineGraphContext, this.$data.backgroundColor);
      };
      d3.interval(renderLineGraphAnalyser, 0);

      // BarGraph関係
      barGraphContext.scale(this._scale, this._scale);
      const renderBarGraphAnalyser = () => {
        // this.$data.audioInstance.renderBarGraphAnalyser(barGraphContext, this.$refs.whole.style.backgroundColor);
        this.$data.audioInstance.renderBarGraphAnalyser(barGraphContext, this.$data.backgroundColor);
      };
      d3.interval(renderBarGraphAnalyser, 0);
    },
    async loadSound() {
      const targets = SOUNDS.agentSounds.concat(SOUNDS.effectSounds);

      // 最初に再生するdroneの音源のみloadする
      const droneSound = SOUNDS.droneSounds.find((sound) => {
        return sound.soundName === this.$data.playingDroneSourceName;
      });

      targets.push(droneSound);

      // 非同期で読み込む。
      await this.$data.audioInstance.loadSoundAsync(targets);

      // 読み込み完了してから再生ボタン活性化させる。
      this.$data.isSoundLoadCompleted = true;
    },
    loadRestSound() {
      // 今再生中のもの以外をlazy風にダウンロードしておく
      const targets = SOUNDS.droneSounds.filter((sound) => {
        return sound.soundName !== this.$data.playingDroneSourceName;
      });

      this.$data.audioInstance.loadSoundAsync(targets);
    },
    initEclipseCanvas() {
      const element = this.$refs.eclipseCanvas;
      const eclipseContext = element.getContext('2d');

      eclipseContext.scale(this._scale, this._scale);

      const setIsEclipseRetrograde = () => {
        this.$data.isEclipseRetrograde = false;
      };

      const renderEclipseGraphAnalyser = () => {
        this.$data.audioInstance.renderEclipseGraphAnalyser(
          eclipseContext,
          this.$refs.whole.style.backgroundColor,
          this.$data.nowRate,
          this.$data.isEclipseRetrograde,
          setIsEclipseRetrograde
        );
      };

      this.$data.d3_objects.renderEclipseGraphAnalyser = d3.interval(renderEclipseGraphAnalyser, 0);
    },
    calculateTimeNow() {
      const time = new Date();
      this.$data.nowDate = time;
    },
    calculateElapsedTime() {
      const time = new Date();
      const difference = differenceInMilliseconds(time, this.$data.createdDate);
      const result = Functions.msToTime(difference);
      this.$data.elapsedTime = result;
    },
    calculateNextEclipseDate() {
      const canditates = CONSTANTS.ECLIPSE_SCHEDULE.map((minutes) => {
        const year = this.$data.nowDate.getFullYear();
        const month = this.$data.nowDate.getMonth();
        const date = this.$data.nowDate.getDate();
        const hours = this.$data.nowDate.getHours();
        const seconds = '00';

        return new Date(year, month, date, hours, minutes, seconds);
      });

      const results = canditates
        .filter((candidate) => {
          const difference = differenceInMilliseconds(candidate, this.$data.nowDate);

          // 負の数は過ぎたものなので、正の候補だけ返す。
          return difference > 0;
        })
        .map((candidate) => {
          const difference = differenceInMilliseconds(candidate, this.$data.nowDate);

          return {
            candidateDate: candidate,
            difference,
          };
        });

      results.sort((a, b) => {
        if (a.difference > b.difference) {
          return 1;
        } else {
          return -1;
        }
      });

      const index = closestIndexTo(results[0].candidateDate, canditates);
      const previousIndex = index === 0 ? canditates.length - 1 : index - 1;

      // 別の計算処理のために一つ前の物も入れておく
      this.$data.previousEclipseDate = canditates[previousIndex];
      this.$data.nextEclipseDate = results[0].candidateDate;
    },
    changeBackGroundColor() {
      const nextBackground = CONSTANTS.BACKGROUND_COLOR_PATTERN.find((item) => {
        return item.soundName === this.$data.playingDroneSourceName;
      });

      const previousBackground = CONSTANTS.BACKGROUND_COLOR_PATTERN.find((item) => {
        return item.soundName === this.$data.playedDroneSourceName;
      });

      if (previousBackground === nextBackground) {
        // 一緒だったら特にこの後の計算する必要ないのですぐ値セットしてしまう（ちなみに初回はplayedもplayingもどっちも同じ値入れている）。
        this.$data.backgroundColor = `rgb(${nextBackground.backgroundColor.r}, ${nextBackground.backgroundColor.g}, ${nextBackground.backgroundColor.b})`;

        return;
      }

      const nowDate = this.$data.nowDate;

      const rate = (() => {
        const n = differenceInMilliseconds(nowDate, this.$data.previousEclipseDate);
        const m = differenceInMilliseconds(this.$data.nextEclipseDate, this.$data.previousEclipseDate);

        // 早く色を変えたいので変化速度2倍速にする
        const changingSpeed = 2;
        const result = (n / m) * changingSpeed;

        if (result > 1) {
          return 1;
        }

        return result;
      })();

      const currentRshouldBe =
        previousBackground.backgroundColor.r +
        (nextBackground.backgroundColor.r - previousBackground.backgroundColor.r) * rate;
      const currentGshouldBe =
        previousBackground.backgroundColor.g +
        (nextBackground.backgroundColor.g - previousBackground.backgroundColor.g) * rate;
      const currentBshouldBe =
        previousBackground.backgroundColor.b +
        (nextBackground.backgroundColor.b - previousBackground.backgroundColor.b) * rate;

      this.$data.backgroundColor = `rgb(${currentRshouldBe}, ${currentGshouldBe}, ${currentBshouldBe})`;
    },
    runMove() {
      // 1匹もいない時は何もしない
      if (this.$data.agents.length === 0) {
        return;
      }

      // functionの呼び出しにするとなぜか重くなる。そこまで影響はないはずだが、一旦コメントアウト
      // const howManyTimes = Functions.calculateTimes(this.$data.agents.length);

      const howManyTimes = (() => {
        // isEclipseMovementがtrueの時、buruburuしだす。
        if (this.$data.eclipseMovementFase === 1) {
          // TBの時はGPU・CPUリソース少ないので無理しない
          if (window.innerWidth < CONSTANTS.TB_BREAK_POINT) {
            return Math.floor(this.$data.agents.length * 0.15);
          }

          return Math.floor(this.$data.agents.length * 0.3);
        }

        if (this.$data.eclipseMovementFase === 2) {
          // TBの時はGPU・CPUリソース少ないので無理しない
          if (window.innerWidth < CONSTANTS.TB_BREAK_POINT) {
            return Math.floor(this.$data.agents.length * 0.4);
          }

          return Math.floor(this.$data.agents.length * 1.7);
        }

        if (this.$data.eclipseMovementFase === 3) {
          // TBの時はGPU・CPUリソース少ないので無理しない
          if (window.innerWidth < CONSTANTS.TB_BREAK_POINT) {
            return Math.floor(this.$data.agents.length * 0.2);
          }

          return Math.floor(this.$data.agents.length * 0.4);
        }

        return Math.floor(Math.random() * 8);
      })();

      for (let i = 1; i <= howManyTimes; i++) {
        // ランダムでagentをピックアップ
        const maximumNum = this.$data.agents.length;
        const randomNum = Math.floor(maximumNum * Math.random());
        const pickedAgent = this.$data.agents[randomNum];

        const pickedAnimation = (() => {
          // eclipseMovementFaseが0より大きい時=eclipse movementが発生
          if (this.$data.eclipseMovementFase > 0) {
            return CONSTANTS.ANIMATION_MAP[`${pickedAgent.type}`][0];
          }

          // ランダムでそのagentに適用させるanimationをピックアップ
          const classMaximumNum = CONSTANTS.ANIMATION_MAP[`${pickedAgent.type}`].length;
          const classRandomNum = Math.floor(classMaximumNum * Math.random());
          return CONSTANTS.ANIMATION_MAP[`${pickedAgent.type}`][classRandomNum];
        })();

        // gain値の決定（agentのtypeによって固定値）
        const gainValue = CONSTANTS.GAIN_VALUE_MAP[pickedAgent.type];

        // panの設定（中心からどれだけ離れているのかをmin: -1, max:1の範囲で取得する）
        const center = this.$data.setting.worldWidth / 2;
        const distanceFromCenter = pickedAgent.x - center;
        const wholeDistance = this.$data.setting.worldWidth / 2;
        const panValue = distanceFromCenter / wholeDistance;

        // 日食の最高潮の時はめちゃくちゃにしたいのですごい数字にする
        const timeOut = this.$data.eclipseMovementFase === 2 ? 9500 : 4000;

        setTimeout(() => {
          // 死にかけのものは動かないし、音も出さない（死ぬときの音作ってもいい）。
          if (pickedAgent.isEndingLife) {
            return;
          }

          pickedAgent.startMovingDate = new Date();
          pickedAgent.class = pickedAnimation.className;
          this.$data.audioInstance.playAgentSound(pickedAnimation.soundName, gainValue, panValue);
        }, Math.random() * timeOut); // runMoveの感覚 -2秒した数字の中で、どこかにならす。2秒間音源続くから、かぶるの防ぐ。
      }
    },
    clearMove() {
      const now = new Date();
      this.$data.agents.forEach((agent) => {
        // class = animationが付与されているかどうか
        const doesNotHaveClass = !agent.class;

        // endLife判定されているかどうか
        const isEndingLife = agent.isEndingLife;

        // end-life以外のクラスがある場合のみ、clearMoveのお掃除メソッドを実行したい。
        if (doesNotHaveClass || isEndingLife) {
          return;
        }

        // agentの音源とmoveが全て2秒間前提なので、2秒間のみ持続させる
        const difference = differenceInMilliseconds(now, agent.startMovingDate);
        if (difference >= 2000) {
          agent.class = '';
        }
      });
    },
    runSwim() {
      this.$data.agents.forEach((agent) => {
        // もし死にかけなら動かない
        if (agent.isEndingLife) {
          return;
        }

        Swim.fixTheta(agent, this.$data.agents);
        Swim.addNoise(agent);
        Swim.makeAStep(agent, this.$data.setting);
      });
    },
    giveBirth() {
      const value = Math.random() * 100;

      // agent_1の判定
      const agentOneRatioResult = value < this.$data.lifeDeathRatios.agent_1.birthRate;
      const agentOneCount = this.$data.agents?.filter((agent) => agent.type === 'agent_1').length;

      if (agentOneRatioResult) {
        // 数がmaxだったら何もしない。
        if (agentOneCount === CONSTANTS.TYPE_MAP[0].max) {
          return;
        }

        // agent_1は少し早め
        const addSpeed = 0;

        const format = this._agentFormat.agent_1;
        const lastId = Functions.findLastId(this.$data.agents);
        Functions.execGiveBirth(this.$data.agents, lastId, format, value, this.$data.setting, addSpeed);
      }

      // agent_2の判定(agent_1のmax値の6~8割を満たしていればagent_2が生まれる環境が整う)
      const agentOneCountResult =
        CONSTANTS.TYPE_MAP[0].max * CONSTANTS.BIRTH_CONDITION.agent_2.min <= agentOneCount &&
        agentOneCount <= CONSTANTS.TYPE_MAP[0].max * CONSTANTS.BIRTH_CONDITION.agent_2.max;

      const agentTwoRatioResult = value < this.$data.lifeDeathRatios.agent_2.birthRate;
      const agentTwoCount = this.$data.agents?.filter((agent) => agent.type === 'agent_2').length;

      if (agentOneCountResult && agentTwoRatioResult) {
        // 数がmaxだったら何もしない。
        if (agentTwoCount === CONSTANTS.TYPE_MAP[1].max) {
          return;
        }

        // agent_2は1より早い
        const addSpeed = Math.random() * 700;

        const format = this._agentFormat.agent_2;
        const lastId = Functions.findLastId(this.$data.agents);
        Functions.execGiveBirth(this.$data.agents, lastId, format, value, this.$data.setting, addSpeed);
      }

      // agent_3の判定(agent_2のmax値の6~8割を満たしていればagent_3が生まれる環境が整う)
      const agentTwoCountResult =
        CONSTANTS.TYPE_MAP[1].max * CONSTANTS.BIRTH_CONDITION.agent_3.min <= agentTwoCount &&
        agentTwoCount <= CONSTANTS.TYPE_MAP[1].max * CONSTANTS.BIRTH_CONDITION.agent_3.max;
      const agentThreeRatioResult = value < this.$data.lifeDeathRatios.agent_3.birthRate;
      const agentThreeCount = this.$data.agents?.filter((agent) => agent.type === 'agent_3').length;

      if (agentTwoCountResult && agentThreeRatioResult) {
        // 数がmaxだったら何もしない。
        if (agentThreeCount === CONSTANTS.TYPE_MAP[2].max) {
          return;
        }

        // agent_3は普通
        const addSpeed = Math.random() * 100;

        const format = this._agentFormat.agent_3;
        const lastId = Functions.findLastId(this.$data.agents);
        Functions.execGiveBirth(this.$data.agents, lastId, format, value, this.$data.setting, addSpeed);
      }

      // agent_4の判定(agent_3のmax値の3~9割を満たしていればagent_4が生まれる環境が整う)
      const agentThreeCountResult =
        CONSTANTS.TYPE_MAP[2].max * CONSTANTS.BIRTH_CONDITION.agent_4.min <= agentThreeCount &&
        agentThreeCount <= CONSTANTS.TYPE_MAP[2].max * CONSTANTS.BIRTH_CONDITION.agent_4.max;
      const agentFourRatioResult = value < this.$data.lifeDeathRatios.agent_4.birthRate;
      const agentFourCount = this.$data.agents?.filter((agent) => agent.type === 'agent_4').length;

      if (agentThreeCountResult && agentFourRatioResult) {
        // 数がmaxだったら何もしない。
        if (agentFourCount === CONSTANTS.TYPE_MAP[3].max) {
          return;
        }

        // agent_4は遅い
        const addSpeed = Math.random() * 10;

        const format = this._agentFormat.agent_4;
        const lastId = Functions.findLastId(this.$data.agents);
        Functions.execGiveBirth(this.$data.agents, lastId, format, value, this.$data.setting, addSpeed);
      }
    },
    endLife() {
      const value = Math.random() * 100;
      const controller = {
        agent_1: false,
        agent_2: false,
        agent_3: false,
        agent_4: false,
      };

      const candidatesIndex = this.$data.agents.map((agent, index) => {
        const type = agent.type;

        if (controller[`${type}`]) {
          // そのタイプで削除済みなら次のタイプへ
          return null;
        }

        if (agent.class) {
          // クラスに何か入っている = runMoveする予定の子なので消さない
          return null;
        }

        const deathRate = this.$data.lifeDeathRatios[`${type}`].deathRate;

        if (deathRate === 0) {
          // 0なら比較するまでもない
          return null;
        }

        if (value < deathRate) {
          controller[`${type}`] = true;
          return index;
        }

        return null;
      });

      // nullを取り除く
      const indexes = candidatesIndex.filter((item) => {
        if (item === 0) return true;

        return item;
      });

      indexes.forEach((index) => {
        // agent ピックアップ
        const targetAgent = this.$data.agents[index];

        // 消滅するアニメーション追加
        targetAgent.class = CONSTANTS.END_LIFE_ANIMATION;

        // clearMove, runMoveを回避するため、templateで分岐するためにプロパティセット
        targetAgent.isEndingLife = true;

        setTimeout(() => {
          const spliceIndex = _.findIndex(this.$data.agents, (agent) => {
            return agent.id === targetAgent.id;
          });

          this.$data.agents.splice(spliceIndex, 1);
        }, 2000);
      });
    },
    handleResize() {
      // 再描画のために一旦ストップ
      this.$data.d3_objects.renderEclipseGraphAnalyser.stop();

      this.$data.setting.worldWidth = window.innerWidth;
      this.$data.setting.worldHeight = window.innerHeight;

      // https://jp.vuejs.org/v2/api/index.html#Vue-nextTick を使用することで、DOMの更新サイクル後に実行できる。実行前にレンダリングすると、スケールは効いてないし、width heightは元のままだし、になる。
      this.$nextTick(() => {
        this.initEclipseCanvas();
      });
    },
    handleOrientationchange() {
      // 再描画のために一旦ストップ
      this.$data.d3_objects.renderEclipseGraphAnalyser.stop();

      this.$data.setting.worldWidth = window.innerWidth;
      this.$data.setting.worldHeight = window.innerHeight;

      this.initEclipseCanvas();
    },
    onRunPause(isPause) {
      this.$data.isPause = isPause;
      const d3_objects = this.$data.d3_objects;

      if (isPause) {
        // 音停止
        this.suspendAudio();

        // 動作系停止
        d3_objects.runMove.stop();
        d3_objects.runSwim.stop();
        d3_objects.calculateElapsedTime.stop();
        d3_objects.giveBirth.stop();
        d3_objects.endLife.stop();
      } else {
        // 初回、環境音の再生をする
        if (!this.$data.isDronePlayed) {
          const diff = differenceInSeconds(this.$data.nextEclipseDate, this.$data.nowDate);

          const offset = (() => {
            // タブレットの時は音源が短いのでoffsetはなし
            if (window.innerWidth < CONSTANTS.TB_BREAK_POINT) {
              return 0;
            }

            // 15分間=900秒からdiffを引けば何秒経過していたのかが分かる。音源は15分尺のものが前提
            return 900 - diff;
          })();

          this.playDroneSound(this.$data.playingDroneSourceName, 0.28, offset, true);
          this.$data.isDronePlayed = true;
        }

        // 音再開
        this.resumeAudio();

        // 動作系再開
        d3_objects.runMove = d3.interval(this.runMove, 6000);
        d3_objects.runSwim = d3.interval(this.runSwim, 0);
        d3_objects.calculateElapsedTime = d3.interval(this.calculateElapsedTime, 1000);
        d3_objects.giveBirth = d3.interval(this.giveBirth, 3000);
        d3_objects.endLife = d3.interval(this.endLife, 3000);
      }
    },
    onRunReset() {
      this.$data.agents.forEach((agent) => {
        const x = Math.random() * this.$data.setting.worldWidth;
        const y = Math.random() * this.$data.setting.worldHeight;

        agent.x = x;
        agent.y = y;
      });
    },
    onRunRestore() {
      this.$data.lifeDeathRatios = _.cloneDeep(CONSTANTS.LIFE_DEATH_RATIOS);
    },
    onAgentFocus(agent) {
      this.$data.focusingAgent = agent;
    },
    onAgentLeave() {
      this.$data.focusingAgent = {};
    },
    onMenuclick() {
      this.$data.isMenuOpen = !this.$data.isMenuOpen;
    },
    onAgentGainerValueChange(gainValue) {
      this.$data.agentGainer.value = gainValue;
      this.$data.audioInstance.agentGainerControl(this.$data.agentGainer.value);
    },
    onDroneGainerValueChange(gainValue) {
      this.$data.droneGainer.value = gainValue;
      this.$data.audioInstance.droneGainerControl(this.$data.droneGainer.value);
    },
    playDroneSound(name, gainValue, offset, isLoop) {
      // tbの時音割れてるので調整
      const fixedGainValue = (() => {
        if (window.innerWidth < CONSTANTS.TB_BREAK_POINT) {
          return gainValue * 0.4;
        }

        return gainValue;
      })();

      this.$data.audioInstance.playDroneSoundLinearRampVer(name, fixedGainValue, offset, isLoop);
    },
    stopDroneSound(name) {
      // this.$data.audioInstance.stopDroneSound(name);
      this.$data.audioInstance.stopDroneSoundLinearRampVer(name);
    },
    resumeAudio() {
      this.$data.audioInstance.resume();
    },
    suspendAudio() {
      this.$data.audioInstance.suspend();
    },
  },
  computed: {
    _canvasWidth() {
      return {
        lineAndBar: 267,
        eclipse: this.$data.setting.worldWidth,
      };
    },
    _canvasHeight() {
      const eclipseHeight = (() => {
        const h = 600;

        if (window.innerWidth < CONSTANTS.TB_BREAK_POINT) {
          // PCの時600にして、TB以下の時に350にしたい
          return h * 0.583;
        }

        return h;
      })();

      return {
        lineAndBar: 102,
        // resizeした時、再描画されるように、this.$data.setting.worldHeightの値を使ってほんの少し値を加算する無意味な計算をする（ここ削除するとheightだけ変わった時にcanvasが再描画されずバグる）
        eclipse: eclipseHeight + this.$data.setting.worldHeight / 100,
      };
    },
    _scale() {
      // 解像度上げるために、devicePixelRatioで設定されている画質+4くらいを初期値にする
      return window.devicePixelRatio + 4;
    },
    _agentFormat() {
      const typeBase = {
        x: (randomValue, worldWidth) => (randomValue / 100) * worldWidth,
        y: (randomValue, worldHeight) => (randomValue / 100) * worldHeight,
        theta: (randomValue) => (randomValue / 100) * 360,
        speed_var: (randomValue, noiseSpeed) => 1 + (randomValue / 100) * noiseSpeed,
        birthDate: null,
        startMovingDate: null,
        isEndingLife: false,
      };

      return {
        agent_1: {
          ...typeBase,
          type: 'agent_1',
          width: CONSTANTS.AGENT_SIZE.agent_1.width,
          height: CONSTANTS.AGENT_SIZE.agent_1.height,
          src: (randomValue) => (randomValue < 70 ? AGENT_SVGS.AGENT_1_SVG : AGENT_SVGS.AGENT_1_INVERSION_SVG),
        },
        agent_2: {
          ...typeBase,
          type: 'agent_2',
          width: CONSTANTS.AGENT_SIZE.agent_2.width,
          height: CONSTANTS.AGENT_SIZE.agent_2.height,
          src: (randomValue) => (randomValue < 50 ? AGENT_SVGS.AGENT_2_SVG : AGENT_SVGS.AGENT_2_INVERSION_SVG),
        },
        agent_3: {
          ...typeBase,
          type: 'agent_3',
          width: CONSTANTS.AGENT_SIZE.agent_3.width,
          height: CONSTANTS.AGENT_SIZE.agent_3.height,
          src: (randomValue) =>
            randomValue < 25
              ? AGENT_SVGS.AGENT_3_1_SVG
              : randomValue < 50
              ? AGENT_SVGS.AGENT_3_2_SVG
              : randomValue < 75
              ? AGENT_SVGS.AGENT_3_3_SVG
              : AGENT_SVGS.AGENT_3_4_SVG,
        },
        agent_4: {
          ...typeBase,
          type: 'agent_4',
          width: CONSTANTS.AGENT_SIZE.agent_4.width,
          height: CONSTANTS.AGENT_SIZE.agent_4.height,
          src: () => AGENT_SVGS.AGENT_4_SVG,
        },
      };
    },
    _isFocusingAgent() {
      return !_.isEmpty(this.$data.focusingAgent);
    },
    _eclipseCanvasClass() {
      if (this.$data.isSoundLoadCompleted) {
        return 'eclipseCanvas eclipseCanvas--show';
      }

      return 'eclipseCanvas';
    },
    _isBalancedEnvironment() {
      const agent_1 = this.$data.agents.filter((agent) => {
        return agent.type === 'agent_1';
      });

      const agent_2 = this.$data.agents.filter((agent) => {
        return agent.type === 'agent_2';
      });

      const agent_3 = this.$data.agents.filter((agent) => {
        return agent.type === 'agent_3';
      });

      const agent_4 = this.$data.agents.filter((agent) => {
        return agent.type === 'agent_4';
      });

      const result = (() => {
        const agent_1_result = agent_1?.length >= CONSTANTS.TYPE_MAP.find((item) => item.name === 'agent_1').max * 0.8;
        const agent_2_result = agent_2?.length >= CONSTANTS.TYPE_MAP.find((item) => item.name === 'agent_2').max * 0.8;
        const agent_3_result = agent_3?.length >= CONSTANTS.TYPE_MAP.find((item) => item.name === 'agent_3').max * 0.8;
        const agent_4_result = agent_4?.length >= CONSTANTS.TYPE_MAP.find((item) => item.name === 'agent_4').max * 0.8;

        return agent_1_result && agent_2_result && agent_3_result && agent_4_result;
      })();

      return result;
    },
  },
  created() {
    this.$Progress.start();
  },
  async mounted() {
    this.initAudio();
    this.calculateTimeNow();
    this.changeBackGroundColor();
    await this.loadSound();
    this.$Progress.finish(); // プログレスバーの終了を宣言

    // this.initSetting();
    this.initTime();
    this.initEclipseCanvas();
    this.calculateNextEclipseDate();

    d3.interval(this.clearMove, 300);
    d3.interval(this.calculateTimeNow, 500);
    d3.interval(this.calculateNextEclipseDate, 500);
    d3.interval(this.changeBackGroundColor, 500);

    window.addEventListener('orientationchange', this.handleOrientationchange);
    window.addEventListener('resize', _.debounce(this.handleResize, 500));

    this.loadRestSound();
  },
  beforeDestroy: function() {
    window.removeEventListener('orientationchange', this.handleOrientationchange);
    window.removeEventListener('resize', _.debounce(this.handleResize, 500));
  },
};
</script>

<style lang="scss" scoped>
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.whole {
  width: 100%;
  height: 100%;
  position: relative;
  overflow-x: hidden;
}

.logo {
  position: absolute;
  top: 50%;
  left: 4%;
  transform: translate(-4%, -50%);
  z-index: 1;
  width: 14%;
  height: 54%;

  animation: fadeIn 0.7s ease-in-out alternate;

  img {
    width: 100%;
    height: 100%;
  }
}

.agent {
  // transition: all 0.1s linear; これ入れると動き面白かったが、safariでうまく挙動しないため、コメントアウトする。
  z-index: 2; // Functionsのその上を行きたい

  &:hover {
    transform: scale(1.1, 1.1) !important;
  }
}

.eclipseCanvas {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;

  &--show {
    opacity: 1;
    transition: opacity 0.7s ease-in-out;
  }
}
</style>
